/**
 * AI Chat — глобальный Zustand-store.
 *
 * Хранит:
 *  - открытость панели (isPanelOpen)
 *  - историю сообщений (messages)
 *  - состояние стрима (isStreaming, currentToolCalls)
 *  - актуальный download-артефакт по сессии
 *
 * Hook `useAiChat` (см. `hooks/api/useAiChat.ts`) поверх стора реализует
 * SSE-стриминг через fetch+ReadableStream. Этот файл — только источник
 * правды по state.
 */

import { create } from 'zustand';

export type AiRole = 'user' | 'assistant';

export interface AiToolCall {
  /** Стабильный id внутри сообщения (для list-key). */
  id: string;
  name: string;
  status: 'running' | 'done' | 'error';
  /** Краткая строка аргументов для tooltip («k=v, k=v»). */
  argsSummary?: string;
}

export interface AiDownloadArtifact {
  export_id: string;
  filename: string;
  rows: number;
  columns: number;
}

export interface AiAttachment {
  name: string;
  size: number;
}

export type AiApprovalStatus = 'pending' | 'approved' | 'denied';

export interface AiApproval {
  /** toolUseId из Agent SDK — стабильный id write-операции. */
  id: string;
  tool: string;
  /** Человекочитаемое действие (например, команда скилла). */
  action: string;
  status: AiApprovalStatus;
}

export interface AiMessage {
  id: string;
  role: AiRole;
  /** Накапливаемый текст (markdown). */
  content: string;
  /** Inline-карточки tool_use, упорядочены по времени. */
  toolCalls: AiToolCall[];
  /** Download-карточки (artifact-ссылки). */
  downloads: AiDownloadArtifact[];
  /** Карточки апрува write-операций (human-in-the-loop). */
  approvals: AiApproval[];
  /** Прикреплённые файлы (только для user). */
  attachments?: AiAttachment[];
  /** true пока сообщение ещё пишется (показываем typing/cursor). */
  isStreaming: boolean;
  /** Ответ остановлен пользователем (кнопка «Стоп») — частичная наработка. */
  stopped?: boolean;
  /** Ошибка, если случилась. */
  error?: string;
  createdAt: number;
}

interface AiState {
  isPanelOpen: boolean;
  togglePanel: () => void;
  openPanel: () => void;
  closePanel: () => void;

  messages: AiMessage[];

  /** Текущий диалог (null = новый, ещё не сохранён). */
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  newChat: () => void;
  hydrate: (msgs: { role: AiRole; content: string; tool_calls?: unknown }[]) => void;

  /** В каком сообщении сейчас идёт стрим. */
  streamingMessageId: string | null;
  isStreaming: boolean;

  /** Появилось ли начало ответа. Управляет AiTyping в шапке message-area. */
  awaitingFirstChunk: boolean;

  // Mutators
  appendUser: (msg: { content: string; attachments?: AiAttachment[] }) => string;
  startAssistant: () => string;
  appendText: (id: string, chunk: string) => void;
  startTool: (id: string, toolCallId: string, name: string) => void;
  finishTool: (
    id: string,
    toolCallId: string,
    payload: { argsSummary?: string; status?: 'done' | 'error' },
  ) => void;
  addDownload: (id: string, artifact: AiDownloadArtifact) => void;
  addApproval: (id: string, approval: AiApproval) => void;
  setApprovalStatus: (toolUseId: string, status: AiApprovalStatus) => void;
  setError: (id: string, error: string) => void;
  finishStream: (id: string) => void;
  /** Остановить стрим пользователем: пометить сообщение «остановлено», сохранив наработку. */
  stopStream: (id: string) => void;
  reset: () => void;
}

function nanoid(): string {
  return Math.random().toString(36).slice(2, 11) + Date.now().toString(36).slice(-4);
}

export const useAiStore = create<AiState>((set) => ({
  isPanelOpen: false,
  togglePanel: () => set((s) => ({ isPanelOpen: !s.isPanelOpen })),
  openPanel: () => set({ isPanelOpen: true }),
  closePanel: () => set({ isPanelOpen: false }),

  messages: [],
  conversationId: null,
  streamingMessageId: null,
  isStreaming: false,
  awaitingFirstChunk: false,

  setConversationId: (id) => set({ conversationId: id }),

  newChat: () =>
    set({
      messages: [],
      conversationId: null,
      streamingMessageId: null,
      isStreaming: false,
      awaitingFirstChunk: false,
    }),

  hydrate: (msgs) =>
    set({
      conversationId: useAiStore.getState().conversationId,
      streamingMessageId: null,
      isStreaming: false,
      awaitingFirstChunk: false,
      messages: msgs.map((m) => ({
        id: nanoid(),
        role: m.role,
        content: m.content,
        toolCalls: Array.isArray(m.tool_calls)
          ? (m.tool_calls as { name?: string }[]).map((t, i) => ({
              id: `h${i}`,
              name: t.name ?? 'tool',
              status: 'done' as const,
            }))
          : [],
        downloads: [],
        approvals: [],
        isStreaming: false,
        createdAt: Date.now(),
      })),
    }),

  appendUser: ({ content, attachments }) => {
    const id = nanoid();
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id,
          role: 'user',
          content,
          toolCalls: [],
          downloads: [],
          approvals: [],
          attachments,
          isStreaming: false,
          createdAt: Date.now(),
        },
      ],
    }));
    return id;
  },

  startAssistant: () => {
    const id = nanoid();
    set((s) => ({
      messages: [
        ...s.messages,
        {
          id,
          role: 'assistant',
          content: '',
          toolCalls: [],
          downloads: [],
          approvals: [],
          isStreaming: true,
          createdAt: Date.now(),
        },
      ],
      streamingMessageId: id,
      isStreaming: true,
      awaitingFirstChunk: true,
    }));
    return id;
  },

  appendText: (id, chunk) =>
    set((s) => ({
      awaitingFirstChunk: false,
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, content: m.content + chunk } : m,
      ),
    })),

  startTool: (id, toolCallId, name) =>
    set((s) => ({
      awaitingFirstChunk: false,
      messages: s.messages.map((m) =>
        m.id === id
          ? {
              ...m,
              toolCalls: [
                ...m.toolCalls,
                { id: toolCallId, name, status: 'running' },
              ],
            }
          : m,
      ),
    })),

  finishTool: (id, toolCallId, { argsSummary, status = 'done' }) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id
          ? {
              ...m,
              toolCalls: m.toolCalls.map((t) =>
                t.id === toolCallId ? { ...t, status, argsSummary } : t,
              ),
            }
          : m,
      ),
    })),

  addDownload: (id, artifact) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, downloads: [...m.downloads, artifact] } : m,
      ),
    })),

  addApproval: (id, approval) =>
    set((s) => ({
      awaitingFirstChunk: false,
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, approvals: [...m.approvals, approval] } : m,
      ),
    })),

  setApprovalStatus: (toolUseId, status) =>
    set((s) => ({
      messages: s.messages.map((m) => ({
        ...m,
        approvals: m.approvals.map((a) => (a.id === toolUseId ? { ...a, status } : a)),
      })),
    })),

  setError: (id, error) =>
    set((s) => ({
      isStreaming: false,
      streamingMessageId: null,
      awaitingFirstChunk: false,
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, error, isStreaming: false } : m,
      ),
    })),

  finishStream: (id) =>
    set((s) => ({
      isStreaming: false,
      streamingMessageId: null,
      awaitingFirstChunk: false,
      messages: s.messages.map((m) => (m.id === id ? { ...m, isStreaming: false } : m)),
    })),

  stopStream: (id) =>
    set((s) => ({
      isStreaming: false,
      streamingMessageId: null,
      awaitingFirstChunk: false,
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, isStreaming: false, stopped: true } : m,
      ),
    })),

  reset: () =>
    set({
      messages: [],
      streamingMessageId: null,
      isStreaming: false,
      awaitingFirstChunk: false,
    }),
}));
